import React from "react";
import { useRouter } from "next/router";
import { gql } from "@apollo/client";

import Seo from "../../components/Seo";
import Error from "../../components/Error";
import Loading from "../../components/Loading";
import FlyerCarousel from "../../components/PdpComponents/FlyerCarousel";
import { CompositionPDPFragments } from "../../components/Composition";
import FlyerCompositionDetails, {
  FlyerCompositionDetailsFragments,
} from "../../components/PdpComponents/FlyerCompositionDetails";

import { buildFlyerCustomizeURL } from "../../utils/urls";
import { getFlyerPdpSeoProps } from "../../utils/getSeoProps";
import { getQueryParam } from "../../utils/querystring";
import fastpanel from "../../utils/fastpanel";
import useFlyerQuery from "../../hooks/useFlyerQuery";
import useServerContext from "../../hooks/useServerContext";

function FlyerPDP() {
  const router = useRouter();
  const { serverContext } = useServerContext();
  const { pathname, query } = router;

  let slug: any = getQueryParam(query, "slug");
  let variationId: any = getQueryParam(query, "variation_id");
  slug = slug && slug.length ? slug[0] : slug;
  variationId =
    variationId && variationId.length ? variationId[0] : variationId;

  const {
    loading,
    error,
    flyer,
  } = useFlyerQuery(slug, FLYER_PDP_QUERY);

  if (error) {
    if (serverContext?.res) {
      const errorCode =
        // @ts-expect-error
        error?.graphQLErrors[0]?.extensions?.response.status || 500;
      serverContext.res.statusCode = errorCode;
    }
    return <Error error={error} />;
  }

  if (loading && !flyer) {
    return (
      <>
        <div className="w-[100vw] overflow-x-hidden">
          <Loading />
        </div>
      </>
    );
  }

  const composition = flyer;
  const { name, variations } = composition;
  const variation = variations && variations.length ? variations[0] : undefined;

  let toggleValue = 0;
  if (variations && variations.length > 0 && variationId) {
    toggleValue = variation && variationId == variation.id ? 1 : 0;
  }
  const selectedComp = toggleValue === 0 ? composition : variation;
  const {
    id,
    coinsPerRecipient,
    eventType,
    premiumLineItems,
    designer,
    artifacts,
    isVariant,
  } = selectedComp;
  const shareImage = artifacts?.media?.asset?.renditions?.jpegLarge?.url;

  const toggleCallback = (selectedIndex: number, method: string) => { 
    if (
      (selectedIndex === 0 || selectedIndex === 1) &&
      variation &&
      selectedIndex !== toggleValue
    ) {
      const newSelectedComp = selectedIndex === 0 ? composition : variation;

      let combinedQuery: any = {
        ...query,
        // Ensure it's overwritten so we can have the new value....
        variation_id: newSelectedComp.id,
      }; 
      // Clear the variation_id if we won't want to use it...
      if (newSelectedComp.isVariant === false) {
        delete combinedQuery["variation_id"];
      }

      const linkHREF = {
        pathname: combinedQuery.pathname || pathname,
        query: combinedQuery,
      };

      router.replace(linkHREF, undefined, { shallow: true });

      fastpanel(
        {
          name: "pbr_element_clicked",
          data: {
            "id": method,
            "compid": newSelectedComp.id,
          }
        }
      );
    }
  }

  const customizeURL = buildFlyerCustomizeURL(id);
  const seoProps = {
    ...getFlyerPdpSeoProps(composition),
    ogImage: shareImage,
  };
  const comps = variation ? [ composition, variation ] : [ composition ];

  return (
    <>
      <Seo {...seoProps} />
      <div className="w-[100vw] overflow-x-hidden">
        <div className="grid max-w-screen-2xl mx-auto my-0 grid-cols-[auto] lg:grid-cols-[auto_375px]">
          <FlyerCarousel
            className="relative max-w-[980px] lg:!mt-16"
            comps={comps}
            toggleValue={toggleValue}
            toggleCallback={toggleCallback}
            customizeURL={customizeURL}
          />
          <FlyerCompositionDetails
            className="after:absolute after:inset-y-0 after:left-full after:w-[25vw] after:content-[' '] after:bg-white"
            comps={comps}
            name={name}
            partner={designer}
            eventType={eventType}
            isVariant={isVariant}
            coinsPerRecipient={coinsPerRecipient}
            premiumLineItems={premiumLineItems}
            shareImage={shareImage}
            customizeURL={customizeURL}
            toggleValue={toggleValue}
            toggleCallback={toggleCallback}
          />
        </div>
      </div>
    </>
  );
}

const FLYER_PDP_QUERY = gql`
  query getFlyer(
    $slug: String!
    $version: String!
  ) {
    flyer: flyerDetail(
      slug: $slug
      version: $version
    ) {
      productType
      composition {
        ...CompositionFragment
        ...FlyerCompositionDetailsFragment
        variations {
          ...CompositionFragment
          ...FlyerCompositionDetailsFragment
        }
      }
    }
  }
  ${CompositionPDPFragments}
  ${FlyerCompositionDetailsFragments}
`;

export default FlyerPDP;
